import { DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { InventoryManagementApi } from '@buspro/web/apis';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-tenant-provisioned-resources',
  standalone: true,
  imports: [NzTableModule, NzTagModule, DatePipe, MatButtonModule, MatIconModule, NzButtonModule, NzIconModule],
  template: `
    <div class="p-8">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-3xl font-bold">Provisioned Resources</h1>
        <button
          nz-button
          nzType="default"
          (click)="onRefresh()"
          [nzLoading]="query.isFetching()"
          title="Refresh resources list"
        >
          <span nz-icon nzType="reload" nzTheme="outline"></span>
          Refresh
        </button>
      </div>

      <nz-table [nzData]="resources()" [nzLoading]="query.isLoading()">
        <thead>
          <tr>
            <th>Item Type</th>
            <th>Name</th>
            <th>SKU</th>
            <th>Status</th>
            <th>Allocated To</th>
            <th>Allocated Date</th>
          </tr>
        </thead>
        <tbody>
          @for (item of resources(); track trackByResourceId($index, item)) {
            <tr>
              <td>
                <nz-tag [nzColor]="item.itemType === 'MDVR_DEVICE' ? 'blue' : 'green'">
                  {{ item.itemType }}
                </nz-tag>
              </td>
              <td>{{ item.itemName }}</td>
              <td>{{ item.itemSku }}</td>
              <td>
                <nz-tag [nzColor]="getStatusColor(item.status)">
                  {{ item.status }}
                </nz-tag>
              </td>
              <td>{{ item.allocatedToBusName || 'Tenant-level' }}</td>
              <td>{{ item.allocatedAt | date: 'short' }}</td>
            </tr>
          }
        </tbody>
      </nz-table>
    </div>
  `,
})
export class TenantProvisionedResourcesPageComponent {
  private route = inject(ActivatedRoute);
  private api = inject(InventoryManagementApi);

  tenantId = this.route.snapshot.params['tid'];

  query = injectQuery(() => ({
    queryKey: ['tenant-provisioned-resources', this.tenantId],
    queryFn: () =>
      lastValueFrom(
        this.api.inventoryControllerGetTenantProvisionedResources({
          tenantId: this.tenantId,
        }),
      ),
    refetchInterval: 30000, // Refetch every 30 seconds in the background
    refetchIntervalInBackground: true, // Continue refetching even when tab is not active
  }));

  resources = () =>
    (this.query.data() as unknown as Array<{
      id: string;
      itemType: string;
      itemName: string;
      itemSku: string;
      status: string;
      allocatedToBusName?: string | null;
      allocatedAt?: Date | null;
    }>) || [];

  // Track previous resource IDs to prevent flickering during updates
  private previousResourceIds = new Set<string>();

  /**
   * Track resource by ID to prevent UI flickering during updates
   */
  trackByResourceId(
    index: number,
    resource: {
      id: string;
      itemType: string;
      itemName: string;
      itemSku: string;
      status: string;
      allocatedToBusName?: string | null;
      allocatedAt?: Date | null;
    },
  ): string {
    // Store the ID for comparison
    if (resource.id) {
      this.previousResourceIds.add(resource.id);
    }
    return resource.id || `resource-${index}`;
  }

  /**
   * Handle manual refresh button click
   */
  onRefresh() {
    this.query.refetch();
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      IN_STOCK: 'default',
      ALLOCATED: 'processing',
      INSTALLED: 'success',
      FAULTY: 'error',
    };
    return colors[status] || 'default';
  }
}
